import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { adminMailApi } from '@/lib/api';
import type { MailSettings } from '@/types';
import { toast } from 'sonner';

export function MailSettingsPanel() {
  const [settings, setSettings] = React.useState<MailSettings | null>(null);
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  React.useEffect(() => {
    adminMailApi
      .getSettings()
      .then(setSettings)
      .catch(() => toast.error('Failed to load mail settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await adminMailApi.updateSettings({
        ...settings,
        password: password || undefined,
      });
      setSettings(updated);
      setPassword('');
      toast.success('Mail settings saved');
    } catch {
      toast.error('Failed to save mail settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await adminMailApi.sendTest();
      toast.success('Test email sent');
    } catch {
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading || !settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mail Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mail Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />
            Enable outbound email
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              placeholder="SMTP host"
              value={settings.host ?? ''}
              onChange={(e) => setSettings({ ...settings, host: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Port"
              value={settings.port}
              onChange={(e) => setSettings({ ...settings, port: Number(e.target.value) })}
            />
            <Input
              placeholder="Username"
              value={settings.username ?? ''}
              onChange={(e) => setSettings({ ...settings, username: e.target.value })}
            />
            <Input
              type="password"
              placeholder={settings.passwordSet ? 'Password (unchanged)' : 'Password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              type="email"
              placeholder="From address"
              value={settings.fromAddress ?? ''}
              onChange={(e) => setSettings({ ...settings, fromAddress: e.target.value })}
              className="sm:col-span-2"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.useTls}
              onChange={(e) => setSettings({ ...settings, useTls: e.target.checked })}
            />
            Use TLS
          </label>

          <div className="flex gap-2">
            <Button type="submit" loading={saving}>
              Save
            </Button>
            <Button type="button" variant="outline" onClick={handleTest} loading={testing}>
              Send test email
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
